import PortfolioConfigData from 'config/portfolio-config.json';
import React, { memo, useCallback, useMemo } from 'react';

import PortfolioTable from './ComposizioneDelPortafoglioTable/ComposizioneDelPortafoglioTable';

import colorsStore from 'shared/store/colorsStore';
import { formatItalianNumber } from 'utils/formatUtils';
import styles from './ComposizioneDelPortafoglio.module.scss';
import { CountervalueData, ProductData } from './types';
import { hideGlobalTooltip, showGlobalTooltip } from './utils';
import GlobalTooltip from './GlobalTooltip';
import { POLIZZE_COLUMN_LABELS, POLIZZE_TITLE, polizzaTipoProdotto, polizzeSubTitle } from './polizze';
import useStrumentiSort from './hooks/useStrumentiSort';

// Shared column widths for fondi and certificati tables (before controvalore insertion)
const SHARED_COLUMN_WIDTHS = [
  152, // ISIN (wide enough for the FAM FoF expand toggle, issue #90)
  100, // Tipo Prodotto
  288, // Nome (300 - 12: keeps the fondi total at 1240 = the scroll wrapper at the stage's max width)
  80, // Valuta
];

// Column widths for fondi (after the shared columns, without controvalore)
const BASE_COLUMN_WIDTHS_FONDI = [
  ...SHARED_COLUMN_WIDTHS,
  100, // Peso sul PTF (%)
  200, // Microcategoria Advice
  120, // Costi totali stimati (%)
  100, // FS / FS Advice
  100, // ESG rating
]; // 9 columns

// Column widths for obbligazionari (without controvalore, no Tipo Prodotto - redundant in bond-only table)
const BASE_COLUMN_WIDTHS_OBBLIGAZIONARI = [
  152, // ISIN (wide enough for the FAM FoF expand toggle, issue #90)
  278, // Nome (300 - 22: keeps the total at 1240 = the scroll wrapper at the stage's max width)
  80, // Valuta
  100, // Peso sul PTF (%)
  150, // Tipo emittente
  100, // Tipo bond
  100, // Paese
  110, // Scadenza
  80, // YTM
  90, // Duration
]; // 10 columns

// Column widths for azioni (without controvalore, no Tipo Prodotto - redundant in stock-only table)
// Total must equal BASE_COLUMN_WIDTHS_OBBLIGAZIONARI total (1240) so first columns align
const BASE_COLUMN_WIDTHS_AZIONI = [
  152, // ISIN (wide enough for the FAM FoF expand toggle, issue #90)
  278, // Nome (300 - 22, see obbligazionari)
  80, // Valuta
  100, // Peso sul PTF (%)
  330, // Microcategoria Advice
  150, // ESG rating
  150, // Dividend Yield
]; // 7 columns

// Column widths for certificati (without Tipo Prodotto and without controvalore)
// Certificate composition table omits Tipo Prodotto (redundant in cert-only table).
// Must NOT use SHARED_COLUMN_WIDTHS — that includes Tipo Prodotto which would
// shift every certificate column by one and break the alignment.
const BASE_COLUMN_WIDTHS_CERTIFICATI = [
  152, // ISIN (wide enough for the FAM FoF expand toggle, issue #90)
  278, // Nome (same as obbligazionari/azioni so ISIN+Nome = 430 and Valuta aligns)
  80, // Valuta
  80, // Peso sul PTF (%)
  150, // Emittente
  150, // Microcategoria
  90, // Data emissione
  90, // Data scadenza
  80, // Costi totali (%)
  150, // Categoria Acepi
  120, // Tipologia
  80, // Protezione
  80, // Barriera down
  80, // Opzione quanto
  90, // Dividend Yield
]; // 15 columns

// Column widths for polizze: the funds table's columns minus FS, as in the dossier
const BASE_COLUMN_WIDTHS_POLIZZE = [
  152, // Codice prodotto
  278, // Nome
  100, // Tipo prodotto
  80, // Valuta
  100, // Peso sul PTF (%)
  200, // Microcategoria
  80, // YTM
  100, // Rating ESG
  90, // Duration
  150, // Commissioni di gestione
  140, // Costi totali stimati
]; // 11 columns (+ Controvalore when the countervalue is known)

// Define sortable columns
const SORTABLE_COLUMNS_FONDI = [
  'peso_sul_ptf',
  'microcategoria_advice',
  'costi_totali_stimati',
  'final_score_advice',
  'rating_ESG',
];
// Insurance guided portfolios have no Final Score Advice: Final Score alone.
const SORTABLE_COLUMNS_FONDI_INSURANCE = SORTABLE_COLUMNS_FONDI.map((column) =>
  column === 'final_score_advice' ? 'final_score' : column,
);
const SORTABLE_COLUMNS_OBBLIGAZIONARI = [
  'peso_sul_ptf',
  'tipo_emittente',
  'tipo_bond',
  'paese',
  'scadenza',
  'ytm',
  'duration',
];
const SORTABLE_COLUMNS_AZIONI = [
  'peso_sul_ptf',
  'microcategoria_advice',
  'rating_ESG',
  'dividend_yield',
];
const SORTABLE_COLUMNS_CERTIFICATI = [
  'peso_sul_ptf',
  'emittente',
  'microcategoria_advice',
  'data_scadenza',
  'costi_totali_stimati',
  'fida_categoria_acepi',
  'fida_tipologia',
  'dividend_yield',
];
const SORTABLE_COLUMNS_POLIZZE = ['peso_sul_ptf', 'microcategoria_advice', 'costi_totali_stimati', 'rating_ESG'];

const CONTROVALORE_WIDTH = 110; // Width for the controvalore column

interface ComposizioneDelPortafoglioProps {
  strumenti_fondi: ProductData[];
  strumenti_obbligazionari: ProductData[];
  strumenti_azioni?: ProductData[];
  strumenti_certificati?: ProductData[];
  strumenti_polizze?: ProductData[];
  countervalue?: CountervalueData;
  isInsurance?: boolean;
}

const ComposizioneDelPortafoglio = memo(
  ({
    strumenti_fondi,
    strumenti_obbligazionari,
    strumenti_azioni,
    strumenti_certificati,
    strumenti_polizze,
    countervalue,
    isInsurance = false,
  }: ComposizioneDelPortafoglioProps) => {
    const colorsConfig = colorsStore((state) => state.colorsConfigStore);

    // Transform fondi data to add formatted controvalore
    const transformedFondi = useMemo((): ProductData[] => {
      if (!strumenti_fondi || strumenti_fondi.length === 0) {
        return [];
      }

      return strumenti_fondi.map((asset) => {
        const transformed = { ...asset };

        // Add formatted controvalore if countervalue data is available
        if (countervalue?.assets && asset.isin && countervalue.assets[asset.isin]) {
          const value = countervalue.assets[asset.isin];
          transformed.controvalore = value !== null && value !== undefined ? formatItalianNumber(value) : '0,00';
        }

        // Format peso_sul_ptf if it exists
        if (asset.peso_sul_ptf !== undefined && asset.peso_sul_ptf !== null) {
          const peso = Number(asset.peso_sul_ptf);
          transformed.peso_sul_ptf = formatItalianNumber(peso);
        }

        // Format costi_totali_stimati if it exists
        if (
          asset.costi_totali_stimati !== undefined &&
          asset.costi_totali_stimati !== null &&
          asset.costi_totali_stimati !== '-'
        ) {
          const costi = Number(asset.costi_totali_stimati);
          if (!isNaN(costi)) {
            transformed.costi_totali_stimati = formatItalianNumber(costi);
          }
        }
        transformed.final_score = asset.final_score ? asset.final_score : '-';
        transformed.final_score_advice = asset.final_score_advice ? asset.final_score_advice : '-';
        transformed.rating_ESG = asset.rating_ESG ? asset.rating_ESG : '-';

        return transformed;
      });
    }, [strumenti_fondi, countervalue]);

    // Transform obbligazionari data to add formatted controvalore
    const transformedObbligazionari = useMemo((): ProductData[] => {
      if (!strumenti_obbligazionari || strumenti_obbligazionari.length === 0) {
        return [];
      }

      return strumenti_obbligazionari.map((asset) => {
        const transformed = { ...asset };

        // Add formatted controvalore if countervalue data is available
        if (countervalue?.assets && asset.isin && countervalue.assets[asset.isin]) {
          const value = countervalue.assets[asset.isin];
          transformed.controvalore = value !== null && value !== undefined ? formatItalianNumber(value) : '0,00';
        }

        // Format peso_sul_ptf if it exists
        if (asset.peso_sul_ptf !== undefined && asset.peso_sul_ptf !== null) {
          const peso = Number(asset.peso_sul_ptf);
          transformed.peso_sul_ptf = formatItalianNumber(peso);
        }

        // Format numeric fields for bonds
        if (asset.ytm !== undefined && asset.ytm !== null && asset.ytm !== '-') {
          const ytm = Number(asset.ytm);
          if (!isNaN(ytm)) {
            transformed.ytm = formatItalianNumber(ytm);
          }
        }

        if (asset.duration !== undefined && asset.duration !== null && asset.duration !== '-') {
          const duration = Number(asset.duration);
          if (!isNaN(duration)) {
            transformed.duration = formatItalianNumber(duration);
          }
        }

        return transformed;
      });
    }, [strumenti_obbligazionari, countervalue]);

    // Transform azioni data to add formatted controvalore
    const transformedAzioni = useMemo((): ProductData[] => {
      if (!strumenti_azioni || strumenti_azioni.length === 0) {
        return [];
      }

      return strumenti_azioni.map((asset) => {
        const transformed = { ...asset };

        // Add formatted controvalore if countervalue data is available
        if (countervalue?.assets && asset.isin && countervalue.assets[asset.isin]) {
          const value = countervalue.assets[asset.isin];
          transformed.controvalore = value !== null && value !== undefined ? formatItalianNumber(value) : '0,00';
        }

        // Format peso_sul_ptf if it exists
        if (asset.peso_sul_ptf !== undefined && asset.peso_sul_ptf !== null) {
          const peso = Number(asset.peso_sul_ptf);
          transformed.peso_sul_ptf = formatItalianNumber(peso);
        }

        // Format dividend_yield if it exists
        if (asset.dividend_yield !== undefined && asset.dividend_yield !== null && asset.dividend_yield !== '-') {
          const dividendYield = Number(asset.dividend_yield);
          if (!isNaN(dividendYield)) {
            transformed.dividend_yield = formatItalianNumber(dividendYield);
          }
        }

        transformed.final_score = asset.final_score ? asset.final_score : '-';
        transformed.final_score_advice = asset.final_score_advice ? asset.final_score_advice : '-';
        transformed.rating_ESG = asset.rating_ESG ? asset.rating_ESG : '-';

        return transformed;
      });
    }, [strumenti_azioni, countervalue]);

    // Transform certificati data to add formatted controvalore
    const transformedCertificati = useMemo((): ProductData[] => {
      if (!strumenti_certificati || strumenti_certificati.length === 0) {
        return [];
      }

      return strumenti_certificati.map((asset) => {
        const transformed = { ...asset };

        // Add formatted controvalore if countervalue data is available
        if (countervalue?.assets && asset.isin && countervalue.assets[asset.isin]) {
          const value = countervalue.assets[asset.isin];
          transformed.controvalore = value !== null && value !== undefined ? formatItalianNumber(value) : '0,00';
        }

        // Format peso_sul_ptf
        if (asset.peso_sul_ptf !== undefined && asset.peso_sul_ptf !== null) {
          const peso = Number(asset.peso_sul_ptf);
          transformed.peso_sul_ptf = formatItalianNumber(peso);
        }

        // Format numeric fields
        if (asset.dividend_yield !== undefined && asset.dividend_yield !== null && asset.dividend_yield !== '-') {
          const dividendYield = Number(asset.dividend_yield);
          if (!isNaN(dividendYield)) {
            transformed.dividend_yield = formatItalianNumber(dividendYield);
          }
        }

        if (asset.costi_totali_stimati !== undefined && asset.costi_totali_stimati !== null && asset.costi_totali_stimati !== '-') {
          const costi = Number(asset.costi_totali_stimati);
          if (!isNaN(costi)) {
            transformed.costi_totali_stimati = formatItalianNumber(costi);
          }
        }

        if (asset.fida_protezione !== undefined && asset.fida_protezione !== null) {
          const protezione = Number(asset.fida_protezione);
          if (!isNaN(protezione)) {
            transformed.fida_protezione = formatItalianNumber(protezione);
          }
        }

        if (asset.fida_barriera_down !== undefined && asset.fida_barriera_down !== null) {
          const barriera = Number(asset.fida_barriera_down);
          if (!isNaN(barriera)) {
            transformed.fida_barriera_down = formatItalianNumber(barriera);
          }
        }

        return transformed;
      });
    }, [strumenti_certificati, countervalue]);

    // Transform polizze data to add formatted controvalore
    const transformedPolizze = useMemo((): ProductData[] => {
      if (!strumenti_polizze || strumenti_polizze.length === 0) {
        return [];
      }

      return strumenti_polizze.map((asset) => {
        const transformed = { ...asset };

        // Add formatted controvalore if countervalue data is available
        if (countervalue?.assets && asset.isin && countervalue.assets[asset.isin]) {
          const value = countervalue.assets[asset.isin];
          transformed.controvalore = value !== null && value !== undefined ? formatItalianNumber(value) : '0,00';
        }

        // Format peso_sul_ptf if it exists
        if (asset.peso_sul_ptf !== undefined && asset.peso_sul_ptf !== null) {
          const peso = Number(asset.peso_sul_ptf);
          transformed.peso_sul_ptf = formatItalianNumber(peso);
        }

        transformed.tipo_prodotto = polizzaTipoProdotto(asset);
        transformed.microcategoria_advice = asset.microcategoria_advice || '-';
        transformed.rating_ESG = asset.rating_ESG || '-';
        ['ytm', 'duration', 'commissioni_gestione', 'costi_totali_stimati'].forEach((field) => {
          transformed[field] = asset[field] != null ? formatItalianNumber(Number(asset[field])) : '-';
        });

        return transformed;
      });
    }, [strumenti_polizze, countervalue]);

    const sortableColumnsFondi = isInsurance ? SORTABLE_COLUMNS_FONDI_INSURANCE : SORTABLE_COLUMNS_FONDI;
    const finalScoreColumnFondi = isInsurance ? 'final_score' : 'final_score_advice';

    const headersFondi = useMemo(() => {
      if (countervalue?.total !== null) {
        return [
          'isin',
          'tipo_prodotto',
          'nome',
          'valuta',
          'controvalore',
          'peso_sul_ptf',
          'microcategoria_advice',
          'costi_totali_stimati',
          finalScoreColumnFondi,
          'rating_ESG',
        ];
      } else {
        return [
          'isin',
          'tipo_prodotto',
          'nome',
          'valuta',
          'peso_sul_ptf',
          'microcategoria_advice',
          'costi_totali_stimati',
          finalScoreColumnFondi,
          'rating_ESG',
        ];
      }
    }, [countervalue, finalScoreColumnFondi]);

    const columnWidthsFondi = useMemo(() => {
      if (countervalue?.total !== null) {
        // Insert controvalore width at position 4 (after valuta)
        return [...BASE_COLUMN_WIDTHS_FONDI.slice(0, 4), CONTROVALORE_WIDTH, ...BASE_COLUMN_WIDTHS_FONDI.slice(4)];
      }
      return BASE_COLUMN_WIDTHS_FONDI;
    }, [countervalue]);

    const headersObbligazionari = useMemo(() => {
      if (countervalue?.total !== null) {
        return [
          'isin',
          'nome',
          'valuta',
          'controvalore',
          'peso_sul_ptf',
          'tipo_emittente',
          'tipo_bond',
          'paese',
          'scadenza',
          'ytm',
          'duration',
        ];
      } else {
        return [
          'isin',
          'nome',
          'valuta',
          'peso_sul_ptf',
          'tipo_emittente',
          'tipo_bond',
          'paese',
          'scadenza',
          'ytm',
          'duration',
        ];
      }
    }, [countervalue]);

    const columnWidthsObbligazionari = useMemo(() => {
      if (countervalue?.total !== null) {
        // Insert controvalore width at position 3 (after valuta)
        return [
          ...BASE_COLUMN_WIDTHS_OBBLIGAZIONARI.slice(0, 3),
          CONTROVALORE_WIDTH,
          ...BASE_COLUMN_WIDTHS_OBBLIGAZIONARI.slice(3),
        ];
      }
      return BASE_COLUMN_WIDTHS_OBBLIGAZIONARI;
    }, [countervalue]);

    const headersAzioni = useMemo(() => {
      if (countervalue?.total !== null) {
        return [
          'isin',
          'nome',
          'valuta',
          'controvalore',
          'peso_sul_ptf',
          'microcategoria_advice',
          'rating_ESG',
          'dividend_yield',
        ];
      } else {
        return [
          'isin',
          'nome',
          'valuta',
          'peso_sul_ptf',
          'microcategoria_advice',
          'rating_ESG',
          'dividend_yield',
        ];
      }
    }, [countervalue]);

    const columnWidthsAzioni = useMemo(() => {
      if (countervalue?.total !== null) {
        // Insert controvalore width at position 3 (after valuta)
        return [...BASE_COLUMN_WIDTHS_AZIONI.slice(0, 3), CONTROVALORE_WIDTH, ...BASE_COLUMN_WIDTHS_AZIONI.slice(3)];
      }
      return BASE_COLUMN_WIDTHS_AZIONI;
    }, [countervalue]);

    const headersCertificati = useMemo(() => {
      const baseHeaders = [
        'isin',
        'nome',
        'valuta',
      ];
      const afterHeaders = [
        'peso_sul_ptf',
        'emittente',
        'microcategoria_advice',
        'data_emissione',
        'data_scadenza',
        'costi_totali_stimati',
        'fida_categoria_acepi',
        'fida_tipologia',
        'fida_protezione',
        'fida_barriera_down',
        'fida_quanto',
        'dividend_yield',
      ];
      if (countervalue?.total !== null) {
        return [...baseHeaders, 'controvalore', ...afterHeaders];
      } else {
        return [...baseHeaders, ...afterHeaders];
      }
    }, [countervalue]);

    const columnWidthsCertificati = useMemo(() => {
      if (countervalue?.total !== null) {
        // Insert controvalore width at position 3 (after valuta)
        const widths = [...BASE_COLUMN_WIDTHS_CERTIFICATI];
        widths.splice(3, 0, CONTROVALORE_WIDTH);
        return widths;
      }
      return BASE_COLUMN_WIDTHS_CERTIFICATI;
    }, [countervalue]);

    // Controvalore sits after Peso, as in the funds table
    const headersPolizze = useMemo(() => {
      const headers = Object.keys(POLIZZE_COLUMN_LABELS).filter((h) => h !== 'controvalore');
      return countervalue?.total !== null ? [...headers.slice(0, 5), 'controvalore', ...headers.slice(5)] : headers;
    }, [countervalue]);

    const columnWidthsPolizze = useMemo(() => {
      if (countervalue?.total !== null) {
        return [...BASE_COLUMN_WIDTHS_POLIZZE.slice(0, 5), CONTROVALORE_WIDTH, ...BASE_COLUMN_WIDTHS_POLIZZE.slice(5)];
      }
      return BASE_COLUMN_WIDTHS_POLIZZE;
    }, [countervalue]);

    const {
      sortConfig: sortConfigFondi,
      sortedData: sortedDataFondi,
      handleSort: handleSortFondi,
    } = useStrumentiSort({
      data: transformedFondi,
      sortableColumns: sortableColumnsFondi,
    });
    const {
      sortConfig: sortConfigObbligazionari,
      sortedData: sortedDataObbligazionari,
      handleSort: handleSortObbligazionari,
    } = useStrumentiSort({
      data: transformedObbligazionari,
      sortableColumns: SORTABLE_COLUMNS_OBBLIGAZIONARI,
    });
    const {
      sortConfig: sortConfigAzioni,
      sortedData: sortedDataAzioni,
      handleSort: handleSortAzioni,
    } = useStrumentiSort({
      data: transformedAzioni,
      sortableColumns: SORTABLE_COLUMNS_AZIONI,
    });
    const {
      sortConfig: sortConfigCertificati,
      sortedData: sortedDataCertificati,
      handleSort: handleSortCertificati,
    } = useStrumentiSort({
      data: transformedCertificati,
      sortableColumns: SORTABLE_COLUMNS_CERTIFICATI,
    });
    const {
      sortConfig: sortConfigPolizze,
      sortedData: sortedDataPolizze,
      handleSort: handleSortPolizze,
    } = useStrumentiSort({
      data: transformedPolizze,
      sortableColumns: SORTABLE_COLUMNS_POLIZZE,
    });

    const columnMappingsFondi = PortfolioConfigData.portfolio.data.strumenti_fondi.columns;
    const columnMappingsObbligazionari = PortfolioConfigData.portfolio.data.strumenti_obbligazionari.columns;
    const columnMappingsAzioni = PortfolioConfigData.portfolio.data.strumenti_azioni.columns;
    const columnMappingsCertificati = PortfolioConfigData.portfolio.data.strumenti_certificati.columns;
    const columnMappingsPolizze = POLIZZE_COLUMN_LABELS;

    // Format cell value to combine final_score and final_score_advice
    const formatCellValue = useCallback((key: string, product: ProductData): string => {
      if (key === 'final_score_advice') {
        return product['final_score'] + '/' + product[key];
      }
      return String(product[key]);
    }, []);

    // These functions don't update component state directly
    const handleTooltipTrigger = useCallback((event: React.MouseEvent, text: string) => {
      showGlobalTooltip(event, text);
    }, []);

    const handleTooltipHide = useCallback(() => {
      hideGlobalTooltip();
    }, []);

    const polizzeSubtitle = polizzeSubTitle(transformedPolizze);

    return (
      <div className={styles.complexTableContainer}>
        {sortedDataPolizze.length > 0 && (
          <>
            <h4 className={styles.tableTitle}>{POLIZZE_TITLE}</h4>
            {polizzeSubtitle && <h5 className={styles.tableTitle}>{polizzeSubtitle}</h5>}
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataPolizze}
                headers={headersPolizze}
                sortConfig={sortConfigPolizze}
                columnMappings={columnMappingsPolizze}
                columnWidths={columnWidthsPolizze}
                colorsConfig={colorsConfig}
                handleSort={handleSortPolizze}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={SORTABLE_COLUMNS_POLIZZE}
                formatCellValue={formatCellValue}
              />
              <GlobalTooltip />
            </div>
          </>
        )}
        {sortedDataFondi.length > 0 && (
          <>
            <h4 className={styles.tableTitle}>Fondi, SICAV, ETF, ETC, ETN</h4>
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataFondi}
                headers={headersFondi}
                sortConfig={sortConfigFondi}
                columnMappings={columnMappingsFondi}
                columnWidths={columnWidthsFondi}
                colorsConfig={colorsConfig}
                handleSort={handleSortFondi}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={sortableColumnsFondi}
                formatCellValue={formatCellValue}
              />
              <GlobalTooltip />
            </div>
          </>
        )}
        {sortedDataObbligazionari.length > 0 && (
          <>
            <h4 className={styles.tableTitle}>Titoli Obbligazionari</h4>
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataObbligazionari}
                headers={headersObbligazionari}
                sortConfig={sortConfigObbligazionari}
                columnMappings={columnMappingsObbligazionari}
                columnWidths={columnWidthsObbligazionari}
                colorsConfig={colorsConfig}
                handleSort={handleSortObbligazionari}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={SORTABLE_COLUMNS_OBBLIGAZIONARI}
                formatCellValue={formatCellValue}
              />
              <GlobalTooltip />
            </div>
          </>
        )}
        {sortedDataAzioni.length > 0 && (
          <>
            <h4 className={styles.tableTitle}>Titoli Azionari</h4>
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataAzioni}
                headers={headersAzioni}
                sortConfig={sortConfigAzioni}
                columnMappings={columnMappingsAzioni}
                columnWidths={columnWidthsAzioni}
                colorsConfig={colorsConfig}
                handleSort={handleSortAzioni}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={SORTABLE_COLUMNS_AZIONI}
                formatCellValue={formatCellValue}
              />
              <GlobalTooltip />
            </div>
          </>
        )}
        {sortedDataCertificati.length > 0 && (
          <>
            <h4 className={styles.tableTitle}>Certificati</h4>
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataCertificati}
                headers={headersCertificati}
                sortConfig={sortConfigCertificati}
                columnMappings={columnMappingsCertificati}
                columnWidths={columnWidthsCertificati}
                colorsConfig={colorsConfig}
                handleSort={handleSortCertificati}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={SORTABLE_COLUMNS_CERTIFICATI}
                formatCellValue={formatCellValue}
              />
              <GlobalTooltip />
            </div>
          </>
        )}
      </div>
    );
  },
);

export default ComposizioneDelPortafoglio;
